import React, { useEffect, useState } from 'react';
import { Webhook, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useFacility } from '../../context/FacilityContext';
import facilitiesService from '../../services/facilitiesService';

// Operator outbound webhooks — the open-API push half. Key events POST
// signed JSON (X-MySMS360-Signature = HMAC-SHA256(secret, body)) to each
// subscribed URL. Wired centrally in the automation dispatcher.
const EVENTS = [
  'payment_received', 'rental_created', 'move_out',
  'invoice_created', 'autopay_failed',
];

const WebhooksCard = () => {
  const { facilityId, currentFacility, refreshFacilities } = useFacility();
  const [subs, setSubs] = useState([]);
  const [form, setForm] = useState(null); // {url, secret, events:Set}
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setSubs(currentFacility?.webhook_subscriptions || []);
  }, [currentFacility?.webhook_subscriptions]);

  const persist = async (next) => {
    setBusy(true);
    try {
      await facilitiesService.update(facilityId, { webhook_subscriptions: next });
      setSubs(next);
      if (refreshFacilities) refreshFacilities();
      toast.success('Webhooks updated.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed.');
    } finally {
      setBusy(false);
    }
  };

  const add = () => {
    if (!form?.url?.startsWith('http')) {
      toast.error('Enter a valid https:// URL.');
      return;
    }
    persist([...subs, {
      url: form.url.trim(),
      secret: form.secret?.trim() || null,
      events: [...(form.events || [])],
    }]);
    setForm(null);
  };

  return (
    <div className="rounded-xl border border-slate-200/80 bg-white p-5 mt-5">
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-[15px] font-semibold text-slate-900 flex items-center gap-2">
          <Webhook className="h-4 w-4 text-blue-600" /> Outbound webhooks
        </h2>
        {!form && (
          <button onClick={() => setForm({ url: '', secret: '', events: new Set(EVENTS) })}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-600 text-[12px] font-medium text-white hover:bg-blue-700">
            <Plus className="h-3.5 w-3.5" /> Add endpoint
          </button>
        )}
      </div>
      <p className="text-[12px] text-slate-500 mb-3">
        POST signed JSON to your systems when things happen here — payments,
        move-ins/outs, invoices, autopay failures. Signature header:
        X-MySMS360-Signature (HMAC-SHA256 of the body with your secret).
      </p>

      {form && (
        <div className="rounded-lg border border-slate-200 p-3 mb-3">
          <input placeholder="https://example.com/hooks/mysms360"
                 className="w-full border border-slate-300 rounded-md px-3 py-1.5 text-[13px] mb-2"
                 value={form.url}
                 onChange={(e) => setForm({ ...form, url: e.target.value })} />
          <input placeholder="Signing secret (recommended)"
                 className="w-full border border-slate-300 rounded-md px-3 py-1.5 text-[13px] mb-2"
                 value={form.secret}
                 onChange={(e) => setForm({ ...form, secret: e.target.value })} />
          <div className="flex flex-wrap gap-3 mb-2">
            {EVENTS.map((ev) => (
              <label key={ev} className="flex items-center gap-1.5 text-[12px] text-slate-600 select-none">
                <input type="checkbox" className="rounded border-slate-300"
                       checked={form.events.has(ev)}
                       onChange={(e) => {
                         const next = new Set(form.events);
                         if (e.target.checked) next.add(ev); else next.delete(ev);
                         setForm({ ...form, events: next });
                       }} />
                {ev}
              </label>
            ))}
          </div>
          <div className="flex gap-2">
            <button onClick={add} disabled={busy}
                    className="px-3.5 py-1.5 rounded-lg bg-blue-600 text-white text-[12px] font-medium hover:bg-blue-700 disabled:opacity-50">
              Save endpoint
            </button>
            <button onClick={() => setForm(null)}
                    className="px-3.5 py-1.5 rounded-lg border border-slate-300 bg-slate-200 text-[12px] font-medium text-slate-700 hover:bg-slate-300">
              Cancel
            </button>
          </div>
        </div>
      )}

      {subs.length === 0 && !form ? (
        <p className="text-[13px] text-slate-400">No endpoints yet.</p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {subs.map((sub, i) => (
            <li key={i} className="py-2.5 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-[13px] font-medium text-slate-900 truncate">{sub.url}</p>
                <p className="text-[11px] text-slate-400">
                  {(sub.events || []).length ? (sub.events || []).join(' · ') : 'all events'}
                  {sub.secret ? ' · signed' : ' · unsigned'}
                </p>
              </div>
              <button onClick={() => persist(subs.filter((_, j) => j !== i))}
                      disabled={busy}
                      className="p-1.5 text-slate-400 hover:text-red-500" title="Remove">
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default WebhooksCard;
